import React, { useMemo, useState } from 'react';
import type { Role, RateMultiplier } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from '@/components/ui/badge';
import { NumberInput } from '@/components/ui/number-input';

type DeletionItem = Role | RateMultiplier;

interface BaseDeletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemIndex: number | null;
  items: DeletionItem[];
  onConfirmDelete: () => void;
}

interface MultiplierDeletionDialogProps extends BaseDeletionDialogProps {
  type: 'cannot_delete' | 'confirm_archive' | 'confirm_simple_delete';
  itemType: 'multiplier';
  items: RateMultiplier[];
  usageDetails: {
    has_booking_history: boolean;
    client_names_with_history: string[];
    client_names_config_only: string[];
    default_usages: string[];
  } | null;
  resolutions: Record<string, { action: string; value?: string; feature?: string; requiresInput?: { type: 'number'; unit: string; position: 'left' | 'right' }; inputValue?: number | null }>;
  onResolutionChange: (usage: string, resolution: Partial<MultiplierDeletionDialogProps['resolutions'][string]>) => void;
  onApplyResolutionsAndDelete: () => void;
  getAlternativeMultipliers: (usage: string, multiplierToDelete: RateMultiplier | null) => { label: string; value: string }[];
}

interface RoleDeletionDialogProps extends BaseDeletionDialogProps {
  type: 'confirm_archive' | 'confirm_delete' | 'confirm_simple_delete';
  itemType: 'role';
  items: Role[];
  usageDetails: {
    has_booking_history: boolean;
    client_names: string[];
  } | null;
}

type DeletionDialogProps = MultiplierDeletionDialogProps | RoleDeletionDialogProps;

export const DeletionDialog: React.FC<DeletionDialogProps> = (props) => {
  const {
    open,
    onOpenChange,
    type,
    itemIndex,
    items,
    onConfirmDelete,
    itemType,
  } = props;

  const itemToDelete = itemIndex !== null ? items[itemIndex] : null;
  const itemName = itemToDelete?.name || '';
  const itemLabel = itemType === 'role' ? 'rate' : 'multiplier';
  const ItemLabel = itemType === 'role' ? 'Rate' : 'Multiplier';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<{ [k: string]: boolean }>({});

  const renderClientList = (items: string[], listKey: string, extraClasses?: string) => {
    if (!items || items.length === 0) return null;
    const isExpanded = !!expanded[listKey];
    const visibleItems = isExpanded ? items : items.slice(0, 5);
    return (
      <div className={extraClasses}>
        <ul className="list-disc list-inside space-y-1">
          {visibleItems.map((name) => <li key={name}>{name}</li>)}
        </ul>
        {items.length > 5 && (
          <button
            type="button"
            className="mt-2 text-xs text-primary underline"
            onClick={() => setExpanded((prev) => ({ ...prev, [listKey]: !isExpanded }))}
          >
            {isExpanded ? 'Show less' : `Show all ${items.length}`}
          </button>
        )}
      </div>
    );
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Handle multiplier-specific "cannot_delete" type
  if (itemType === 'multiplier' && type === 'cannot_delete') {
    const multiplierProps = props as MultiplierDeletionDialogProps;
    const {
      usageDetails,
      resolutions,
      onResolutionChange,
      onApplyResolutionsAndDelete,
      getAlternativeMultipliers,
    } = multiplierProps;

    const defaultUsages = usageDetails?.default_usages || [];
    const unresolvedUsages = defaultUsages.filter((usage) => {
      const r = resolutions[usage];
      return !r || (r.action === 'change_multiplier' && !r.value) || (r.requiresInput && (r.inputValue === null || r.inputValue === undefined || r.inputValue <= 0));
    });
    const hasHistory = (usageDetails?.client_names_with_history?.length || 0) > 0;
    const canResolveAndDelete = !hasHistory && unresolvedUsages.length === 0;

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent centerMode="viewport">
          <DialogHeader>
            <DialogTitle>Cannot Delete {ItemLabel}</DialogTitle>
            <DialogDescription>
              The {itemLabel} "{itemName}" is used in your default settings and can't be deleted until you resolve the conflicts below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary">{defaultUsages.length} {defaultUsages.length === 1 ? 'conflict' : 'conflicts'}</Badge>
            {!canResolveAndDelete && (
              <span className="text-xs text-muted-foreground">
                {unresolvedUsages.length} unresolved
              </span>
            )}
          </div>
          <div className="py-4 text-sm space-y-4">
            {(usageDetails?.default_usages || []).map((usage, index) => {
              const options = getAlternativeMultipliers(usage, itemToDelete as RateMultiplier);
              const currentResolution = resolutions[usage];

              return (
                <div key={usage} className="p-3 border rounded-md bg-muted/50">
                  <p className="font-medium mb-3">Conflict: Your default {usage.replace(':', ' - ')} settings</p>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-1">
                      <Label htmlFor={`resolution-${index}`} className="text-sm">Choose a resolution:</Label>
                    </div>
                    <div className="flex items-start gap-2">
                      {currentResolution?.requiresInput && onResolutionChange && (
                        <div className="flex flex-col">
                          <NumberInput
                            unit={currentResolution.requiresInput.unit}
                            unitPosition={currentResolution.requiresInput.position as 'left' | 'right'}
                            value={currentResolution.inputValue ?? ''}
                            onChange={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : null;
                              onResolutionChange(usage, { inputValue: val });
                            }}
                            className="w-28"
                            placeholder="e.g. 50"
                          />
                          {(currentResolution.inputValue === null || currentResolution.inputValue === undefined || currentResolution.inputValue <= 0) && (
                            <p className="text-xs text-destructive mt-1">Value is required</p>
                          )}
                        </div>
                      )}
                      <Select
                        onValueChange={(valueStr) => {
                          if (valueStr && onResolutionChange) {
                            onResolutionChange(usage, JSON.parse(valueStr));
                          }
                        }}
                        value={currentResolution ? JSON.stringify({
                          action: currentResolution.action,
                          feature: currentResolution.feature,
                          value: currentResolution.value,
                          requiresInput: currentResolution.requiresInput,
                          // DO NOT include inputValue here
                        }) : undefined}
                      >
                        <SelectTrigger className="w-full sm:w-[250px] bg-background">
                          <SelectValue placeholder="Choose a resolution..." />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}

            {(usageDetails?.client_names_config_only || []).length > 0 && (
               <div className="p-3 border rounded-md bg-muted/50">
                  <p className="font-medium mb-2">Client Configurations</p>
                  <p className="text-muted-foreground">This {itemLabel} is also used by the following clients. Deleting it will also remove it from their configurations.</p>
                  {renderClientList(usageDetails?.client_names_config_only || [], 'configOnly', 'mt-2')}
               </div>
            )}

            {(usageDetails?.client_names_with_history || []).length > 0 && (
               <div className="p-3 border border-destructive/50 rounded-md bg-destructive/10 text-destructive-foreground">
                  <p className="font-medium mb-2">Historical Usage</p>
                  <p className="text-destructive-foreground/90">This {itemLabel} has been used in past bookings for the following clients and cannot be deleted. You must update their configurations manually before deleting.</p>
                  {renderClientList(usageDetails?.client_names_with_history || [], 'history', 'mt-2')}
               </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await Promise.resolve(onApplyResolutionsAndDelete());
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={!canResolveAndDelete || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Resolving…' : 'Resolve & Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle "confirm_archive" type for both roles and multipliers
  if (type === 'confirm_archive') {
    const usageDetails = props.usageDetails;
    const clientNames = itemType === 'role' 
      ? (usageDetails as RoleDeletionDialogProps['usageDetails'])?.client_names || []
      : (usageDetails as MultiplierDeletionDialogProps['usageDetails'])?.client_names_with_history || [];

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent centerMode="viewport">
          <DialogHeader>
            <DialogTitle>Archive {ItemLabel}?</DialogTitle>
            <DialogDescription>
              The {itemLabel} for "{itemName}" has been used in past bookings and cannot be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm">
            <p>You can archive it instead. Archiving will hide it from new bookings and configurations, but preserve it for historical records.</p>
            {clientNames.length > 0 && (
               <div className="mt-4">
                  <p className="font-medium mb-2">Used in bookings for:</p>
                  <div className="bg-muted p-3 rounded-md">
                    {renderClientList(clientNames, 'archiveClients')}
                  </div>
               </div>
            )}
            <p className="mt-4 font-semibold">Are you sure you want to archive this {itemLabel}?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await Promise.resolve(onConfirmDelete());
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Archiving…' : `Archive ${ItemLabel}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle "confirm_delete" type (roles only - when used in client configs but no booking history)
  if (type === 'confirm_delete') {
    const roleProps = props as RoleDeletionDialogProps;
    const clientNames = roleProps.usageDetails?.client_names || [];

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent centerMode="viewport">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              The {itemLabel} for "{itemName}" is used in some client configurations.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm">
            <p>Deleting this {itemLabel} will also remove it from the following clients' configurations. This action cannot be undone.</p>
            {clientNames.length > 0 && (
              <div className="text-muted-foreground bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                {renderClientList(clientNames, 'softBlockedClients')}
              </div>
            )}
            <p className="mt-4 font-semibold">Are you sure you want to proceed?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await Promise.resolve(onConfirmDelete());
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Deleting…' : `Delete ${ItemLabel}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle "confirm_simple_delete" type for both roles and multipliers
  if (type === 'confirm_simple_delete') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent centerMode="viewport">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the {itemLabel} for "{itemName}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await Promise.resolve(onConfirmDelete());
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Deleting…' : `Delete ${ItemLabel}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};
